import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  TrendingUp, 
  TrendingDown,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  DollarSign,
  Target,
  BarChart3,
  Download,
  RefreshCw,
  Server,
  Cpu,
  HardDrive,
  Wifi
} from 'lucide-react';

interface DashboardOverview {
  kpis: {
    systemHealth: string;
    optimizationSuccessRate: number;
    averageResponseTime: number;
    activeUsers: number;
    dailyOptimizations: number;
    estimatedRevenue: number;
    systemLoad: number;
    scalingRecommendation: string;
  };
  optimizationMetrics: any;
  performanceMetrics: any;
  alertStatus: {
    alerts: Array<{
      type: string;
      severity: string;
      message: string;
      value: number;
      threshold: number;
    }>;
    healthy: boolean;
  };
  capacityMetrics: any;
}

export const MonitoringDashboardPage: React.FC = () => {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>('day');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchDashboardOverview();
    
    if (autoRefresh) {
      const interval = setInterval(fetchDashboardOverview, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [timeRange, autoRefresh]);

  const fetchDashboardOverview = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/monitoring/dashboard-overview?timeRange=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setOverview(data.overview);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch dashboard data');
      }
    } catch (error) {
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'text-green-600';
      case 'degraded':
        return 'text-yellow-600';
      case 'unhealthy':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && !overview) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={fetchDashboardOverview}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">No dashboard data available</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">System Monitoring Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Real-time monitoring and analytics for pricing optimization system
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto' : 'Manual'}
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* System Health Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Health Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className={`text-2xl font-bold ${getHealthColor(overview.kpis.systemHealth)}`}>
                  {overview.kpis.systemHealth === 'healthy' ? (
                    <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  ) : (
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                  )}
                </div>
                <div className="text-sm font-medium">System Health</div>
                <div className="text-xs text-muted-foreground capitalize">
                  {overview.kpis.systemHealth}
                </div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {formatPercentage(overview.kpis.optimizationSuccessRate)}
                </div>
                <div className="text-sm font-medium">Success Rate</div>
                <div className="text-xs text-muted-foreground">Optimization success</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatTime(overview.kpis.averageResponseTime)}
                </div>
                <div className="text-sm font-medium">Avg Response Time</div>
                <div className="text-xs text-muted-foreground">System performance</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {overview.kpis.activeUsers}
                </div>
                <div className="text-sm font-medium">Active Users</div>
                <div className="text-xs text-muted-foreground">Last 24 hours</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Daily Optimizations</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview.kpis.dailyOptimizations}</div>
              <p className="text-xs text-muted-foreground">
                +12% from yesterday
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Estimated Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(overview.kpis.estimatedRevenue)}</div>
              <p className="text-xs text-muted-foreground">
                +8% from yesterday
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">System Load</CardTitle>
              <Server className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatPercentage(overview.kpis.systemLoad)}</div>
              <Progress value={overview.kpis.systemLoad * 100} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Scaling</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold capitalize">
                {overview.kpis.scalingRecommendation.replace('_', ' ')}
              </div>
              <p className="text-xs text-muted-foreground">
                Recommendation
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">CPU Usage</span>
                </div>
                <div className="text-2xl font-bold">
                  {formatPercentage(overview.performanceMetrics.cpuUsage)}
                </div>
                <Progress value={overview.performanceMetrics.cpuUsage * 100} />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Memory Usage</span>
                </div>
                <div className="text-2xl font-bold">
                  {formatPercentage(overview.performanceMetrics.memoryUsage)}
                </div>
                <Progress value={overview.performanceMetrics.memoryUsage * 100} />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Wifi className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">Active Jobs</span>
                </div>
                <div className="text-2xl font-bold">
                  {overview.performanceMetrics.activeJobs}
                </div>
                <div className="text-xs text-muted-foreground">
                  In progress
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-medium">Peak Response</span>
                </div>
                <div className="text-2xl font-bold">
                  {formatTime(overview.performanceMetrics.peakResponseTime)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Slowest request
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        {overview.alertStatus.alerts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Active Alerts ({overview.alertStatus.alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {overview.alertStatus.alerts.map((alert, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge className={getSeverityColor(alert.severity)}>
                        {alert.severity}
                      </Badge>
                      <div>
                        <div className="font-medium">{alert.message}</div>
                        <div className="text-sm text-muted-foreground">
                          Current: {alert.value.toFixed(2)}, Threshold: {alert.threshold.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Acknowledge
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Capacity Planning */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Capacity Planning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="text-sm font-medium">Current Load</div>
                <div className="text-2xl font-bold">
                  {formatPercentage(overview.capacityMetrics.currentLoad)}
                </div>
                <Progress value={overview.capacityMetrics.currentLoad * 100} />
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Peak Load</div>
                <div className="text-2xl font-bold">
                  {formatPercentage(overview.capacityMetrics.peakLoad)}
                </div>
                <Progress value={overview.capacityMetrics.peakLoad * 100} />
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Estimated Cost</div>
                <div className="text-2xl font-bold">
                  {formatCurrency(overview.capacityMetrics.estimatedCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  This week
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-900">Optimization Trend</span>
              </div>
              <div className="text-blue-800">
                {overview.capacityMetrics.optimizationTrend === 'increasing' ? (
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    Optimizations are increasing
                  </span>
                ) : overview.capacityMetrics.optimizationTrend === 'decreasing' ? (
                  <span className="flex items-center gap-1">
                    <TrendingDown className="h-4 w-4" />
                    Optimizations are decreasing
                  </span>
                ) : (
                  <span>Optimizations are stable</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
